// Public Terms of Service — served unauthenticated at /terms.
// Registered with Meta (App Review) as the app's Terms of Service URL.
// Per Dan: must explicitly disclose that data collected via Meta
// integrations is used for AI training (§6). Mirrored in /privacy §3.
export default function Terms() {
  return (
    <div className="min-h-screen bg-white px-6 py-12 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: July 3, 2026</p>

      <div className="space-y-6 text-gray-700 leading-relaxed">
        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">1. Agreement</h2>
          <p>
            These Terms of Service ("Terms") govern your use of AdMoji AdManager ("AdMoji", "the Service",
            "we", "our", "us") at adsmanager.admoji.com. By creating an account or using the Service you
            agree to these Terms and to our{" "}
            <a href="/privacy" className="text-indigo-600 underline">Privacy Policy</a>. If you use the
            Service on behalf of a company, you represent that you can bind that company to these Terms.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">2. The Service</h2>
          <p>
            AdMoji is an AI-powered platform for creating, managing, and analyzing advertising creatives —
            including AI-generated ad images and copy, brand intelligence, competitive research, and
            performance analytics for connected ad accounts. Features may change, improve, or be withdrawn
            as the product evolves.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">3. Accounts & Eligibility</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>You must be at least 18 and provide accurate account information.</li>
            <li>You are responsible for safeguarding your login and for all activity under your account.</li>
            <li>You may sign in with email/password or Google Sign-In.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">4. Plans, Credits & Billing</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              The Service operates on subscription plans with a monthly credit allowance; AI actions
              (generation, analysis, research) consume credits. Additional one-time credit top-ups may be
              offered.
            </li>
            <li>Payments are processed by Stripe and renew monthly until cancelled.</li>
            <li>
              Unused plan credits expire at each monthly renewal and do not roll over unless stated
              otherwise. Credits have no cash value and are non-transferable.
            </li>
            <li>
              You can cancel or change plans at any time; changes take effect per the billing flow shown at
              purchase. Except where required by law, payments are non-refundable.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">5. Connected Accounts (Meta & Others)</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              When you connect a Meta (Facebook) account you authorize us to access the ad accounts you
              select — including campaigns, ad sets, ads, creatives, and performance insights — using
              Meta's APIs, in accordance with the permissions you grant.
            </li>
            <li>
              You must have the rights to connect any account you connect, and your use of connected
              platforms remains subject to their terms (including the Meta Platform Terms). AdMoji is not
              affiliated with, or endorsed by, Meta Platforms, Inc.
            </li>
            <li>You can disconnect at any time; disconnecting deletes the synced data as described in our Privacy Policy.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">6. Data Collection & AI Training</h2>
          <p>
            <strong>
              You acknowledge and agree that we collect data obtained through the Service — including data
              from connected Meta accounts (such as campaign structures, ad creatives, targeting summaries,
              and performance metrics), content you upload, and your usage of the Service — and that we may
              use this data to train, fine-tune, evaluate, and improve our AI models and
              creative-intelligence systems.
            </strong>{" "}
            Where practical, data used for training is aggregated or de-identified. Insights derived from
            training (e.g. improved model behavior) do not expose your private data to other customers. See
            our Privacy Policy for full details on data handling and deletion.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">7. Your Content & Generated Output</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              You retain ownership of the content you upload (brand assets, product photos, copy). You grant
              us a license to host, process, and display it as needed to provide the Service (including the
              uses described in §6).
            </li>
            <li>
              Subject to these Terms and payment of applicable fees, we assign to you our rights in the ad
              creatives generated for your account, to the extent we hold such rights. AI-generated content
              may have limited or no copyright protection in some jurisdictions.
            </li>
            <li>
              Similar or identical outputs may be generated for other customers; we do not guarantee
              exclusivity of any generated concept, style, or copy.
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">8. Acceptable Use</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li>No unlawful, deceptive, or infringing advertising; you are responsible for the claims made in ads you publish.</li>
            <li>No attempts to breach security, abuse rate limits, scrape the Service, or resell access without our written consent.</li>
            <li>No uploading content you lack rights to, or content that is hateful, sexually explicit, or targets minors.</li>
            <li>You are responsible for ensuring ads you run comply with the policies of the platforms where you run them (e.g. Meta Advertising Standards).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">9. AI Disclaimer</h2>
          <p>
            AI outputs can be inaccurate, incomplete, or unsuitable. You must review generated creatives,
            copy, and recommendations before use. We make no guarantee of advertising performance, approval
            by any ad platform, or fitness for a particular purpose.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">10. Third-Party Services</h2>
          <p>
            The Service relies on third-party providers (AI model providers, payment processing, hosting,
            content delivery, and data APIs). Their availability affects the Service, and their terms apply
            to their processing. We are not responsible for third-party outages or changes.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">11. Termination</h2>
          <p>
            You may stop using the Service and cancel at any time. We may suspend or terminate accounts
            that violate these Terms, create risk for us or others, or are required to be terminated by
            law. Upon termination we will delete your data as described in the Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">12. Disclaimers & Limitation of Liability</h2>
          <p>
            The Service is provided "as is" and "as available", without warranties of any kind, express or
            implied. To the maximum extent permitted by law, our total liability for any claims arising out
            of or relating to the Service is limited to the amounts you paid us in the 12 months before the
            claim, and we are not liable for indirect, incidental, special, consequential, or punitive
            damages, or lost profits, revenues, or data.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">13. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. Material changes will be posted on this page with
            an updated date; continued use of the Service after changes take effect constitutes acceptance.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">14. Contact</h2>
          <p>
            Questions about these Terms:{" "}
            <a href="mailto:support@admoji.com" className="text-indigo-600 underline">support@admoji.com</a>
          </p>
        </section>
      </div>
    </div>
  );
}
